import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDERS = [
  { key: '{estimate_number}', label: 'Estimate # (EST-2026-...)' },
  { key: '{sender_name}',     label: 'Sender name (your business)' },
  { key: '{client_name}',     label: 'Client full name' },
  { key: '{client_first_name}', label: 'Client first name (auto)' },
  { key: '{estimate_title}',  label: 'Estimate title' },
];

/**
 * Edit the default email subject + body for both English and French.
 * Templates support {estimate_number}, {sender_name}, {client_name},
 * {client_first_name}, {estimate_title}.
 */
export default function EmailDefaultsDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    default_subject_en: '',
    default_subject_fr: '',
    default_message_en: '',
    default_message_fr: '',
  });
  const [tab, setTab] = useState('en');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/email/settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(prev => ({ ...prev, ...d })); })
      .finally(() => setLoading(false));
  }, [open]);

  const update = (key, value) => setData(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/email/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          default_subject_en: data.default_subject_en,
          default_subject_fr: data.default_subject_fr,
          default_message_en: data.default_message_en,
          default_message_fr: data.default_message_fr,
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        toast.error(`Save failed — ${err.slice(0, 80)}`);
        return;
      }
      toast.success('Email defaults saved');
      if (onSaved) onSaved(data);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  const subjectKey = tab === 'fr' ? 'default_subject_fr' : 'default_subject_en';
  const messageKey = tab === 'fr' ? 'default_message_fr' : 'default_message_en';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>
            Edit default email templates
          </DialogTitle>
          <p className="text-xs text-[#6B7A73]">
            Used as the starting subject + body whenever you click Email on an estimate.
            You can always edit before sending.
          </p>
        </DialogHeader>

        <div className="flex gap-2 border-b border-[#E6EBE8] -mb-2">
          <TabButton active={tab === 'en'} onClick={() => setTab('en')} testid="defaults-tab-en">English</TabButton>
          <TabButton active={tab === 'fr'} onClick={() => setTab('fr')} testid="defaults-tab-fr">French</TabButton>
        </div>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#2D6B68]" /></div>
        ) : (
          <div className="space-y-4 pt-3">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold block mb-1.5">Subject</label>
              <Input
                value={data[subjectKey]}
                onChange={e => update(subjectKey, e.target.value)}
                className="bg-white border-[#E6EBE8] rounded-md"
                placeholder="e.g. Estimate {estimate_number} — {sender_name}"
                data-testid={`defaults-subject-${tab}`}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-[#6B7A73] font-semibold block mb-1.5">Message body</label>
              <Textarea
                value={data[messageKey]}
                onChange={e => update(messageKey, e.target.value)}
                rows={9}
                className="bg-white border-[#E6EBE8] rounded-md text-sm font-mono"
                placeholder="Hi {client_first_name},..."
                data-testid={`defaults-message-${tab}`}
              />
            </div>
            <div className="bg-[#F9FAFA] border border-[#E6EBE8] rounded-md p-3">
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7A73] font-semibold mb-2">
                Available placeholders
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs">
                {PLACEHOLDERS.map(p => (
                  <div key={p.key} className="flex items-center gap-2">
                    <code className="text-[#2D6B68] font-mono text-[11px]">{p.key}</code>
                    <span className="text-[#6B7A73]">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E6EBE8]">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="bg-[#2D6B68] text-white hover:bg-[#235552]"
            data-testid="defaults-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save defaults
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-[#2D6B68] text-[#1A1D1C]'
          : 'border-transparent text-[#6B7A73] hover:text-[#1A1D1C]'
      }`}
    >
      {children}
    </button>
  );
}
